import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pager, ViewToggle, ConfirmModal, PageSpinner } from '@/components/common';
import { useFundamentals, getFundamentalName } from '@/hooks/useFundamentals';
import { TIP_TYPES } from '@/data/tipTypes';
import { useTips, useCreateTip, useUpdateTip, useDeleteTip } from '@/hooks/useTips';
import type { TipItem } from '@/api/types';
import { fmtCond } from '@/utils/formatters';
import { TipFormModal, ALL_LANG_CODES, defaultLangTexts, TRANS_LANGS } from './TipFormModal';
import type { TipFormState, DeleteTipTarget, LangText } from './TipFormModal';

// ── Translation helpers ───────────────────────────────────────────────────────
// Keys use convention: "{langCode}_{field}" e.g. "en_tipText", "en_name", "en_crecerPaired"

function buildTranslations(
  addedLangs: string[],
  langTexts: Record<string, LangText>,
  tipType: 'reconocer' | 'crecer',
): Record<string, string> {
  const result: Record<string, string> = {};
  for (const code of addedLangs) {
    const lt = langTexts[code];
    if (!lt) continue;
    if (lt.name) result[`${code}_name`] = lt.name;
    if (lt.tipText) result[`${code}_tipText`] = lt.tipText;
    if (tipType === 'reconocer' && lt.crecerPaired) result[`${code}_crecerPaired`] = lt.crecerPaired;
  }
  return result;
}

function langTextsFromTranslations(translations: Record<string, string>): {
  langTexts: Record<string, LangText>;
  addedLangs: string[];
} {
  const langTexts = defaultLangTexts();
  const langSet = new Set<string>();

  for (const [key, value] of Object.entries(translations)) {
    const underscoreIdx = key.indexOf('_');
    if (underscoreIdx < 1) continue;
    const code = key.slice(0, underscoreIdx);
    const field = key.slice(underscoreIdx + 1);
    if (!TRANS_LANGS.find(l => l.code === code)) continue;
    langSet.add(code);
    if (!langTexts[code]) langTexts[code] = { name: '', tipText: '', crecerPaired: '' };
    if (field === 'name') langTexts[code].name = value;
    else if (field === 'tipText') langTexts[code].tipText = value;
    else if (field === 'crecerPaired') langTexts[code].crecerPaired = value;
  }

  const addedLangs = ALL_LANG_CODES.filter(c => langSet.has(c));
  return { langTexts, addedLangs };
}

function localizedTipName(tip: TipItem, lang: string): string {
  if (lang === 'es') return tip.tipName;
  return tip.translations[`${lang}_name`] || tip.tipName;
}

function localizedTipText(tip: TipItem, lang: string): string {
  if (lang === 'es') return tip.tipText;
  return tip.translations[`${lang}_tipText`] || tip.tipText;
}

function makeDefault(): TipFormState {
  return {
    open: false, editId: null, fund: '', behavior: '',
    thrMin: '0', thrMax: '100',
    tipType: 'reconocer', name: '', tipText: '', crecerPaired: '', active: true,
    addedLangs: [],          // create mode: user triggers translations manually
    previewLang: 'es', langTexts: defaultLangTexts(),
  };
}

export function TipsSubTab() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.split('-')[0];

  const [view, setView] = useState<'cards' | 'list'>('cards');
  const [search, setSearch] = useState('');
  const [fundFilter, setFundFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(9);
  const [form, setForm] = useState<TipFormState>(makeDefault);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [deleteTip, setDeleteTip] = useState<DeleteTipTarget | null>(null);
  const [activeMap, setActiveMap] = useState<Record<string, boolean>>({});

  const { data: fundamentals = [] } = useFundamentals();
  const { data: tipsData = [], isLoading: loadingTips } = useTips();
  const tips = tipsData as unknown as (TipItem & { tipCrecerText?: string })[];
  const createTip = useCreateTip();
  const updateTip = useUpdateTip();
  const deleteTipMutation = useDeleteTip();

  const filtered = tips.filter(tip => {
    const q = search.toLowerCase();
    const matchSearch = !q ||
      localizedTipName(tip, lang).toLowerCase().includes(q) ||
      localizedTipText(tip, lang).toLowerCase().includes(q);
    const matchFund = !fundFilter || tip.fundamentalId === fundFilter;
    const matchType = !typeFilter || tip.tipType === typeFilter;
    return matchSearch && matchFund && matchType;
  });
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);
  const fundOf = (id: string) => fundamentals.find(f => f.id === id);

  function tipTypeLabel(id: string) {
    const entry = TIP_TYPES.find(tt => tt.id === id);
    if (!entry) return id;
    return entry.labels[lang] ?? entry.labels['es'];
  }

  function openForm(editId: string | null = null) {
    setSaveErrors([]);
    if (editId) {
      const tip = tips.find(x => x.id === editId);
      if (tip) {
        const { langTexts, addedLangs } = langTextsFromTranslations(tip.translations ?? {});
        setForm({
          open: true, editId,
          fund: tip.fundamentalId,
          behavior: tip.translations?.['behaviorId'] ?? 'skip-validation',
          thrMin: String(tip.threshold),
          thrMax: tip.thresholdMax != null ? String(tip.thresholdMax) : '100',
          tipType: tip.tipType, name: tip.tipName,
          tipText: tip.tipText,
          crecerPaired: tip.translations?.['es_crecerPaired'] ?? tip.tipCrecerText ?? '',
          active: activeMap[tip.id] ?? tip.active,
          addedLangs,
          previewLang: 'es', langTexts,
        });
        return;
      }
    }
    setForm({ ...makeDefault(), open: true });
  }

  function closeForm() {
    setSaveErrors([]);
    setForm(f => ({ ...f, open: false }));
  }

  function handleSave() {
    const errs: string[] = [];
    if (!form.fund) errs.push(t('admin.tipFormFundamental'));
    if (!form.editId && !form.behavior) errs.push(t('admin.tipFormDailyHabit'));

    if (form.tipType === 'reconocer') {
      if (!form.name.trim())         errs.push(`${t('admin.tipFormName')} (ES)`);
      if (!form.tipText.trim())      errs.push(`${t('admin.tipFieldObserved')} (ES)`);
      if (!form.crecerPaired.trim()) errs.push(`${t('admin.tipFieldGrowthSuggestion')} (ES)`);
      for (const code of form.addedLangs) {
        const lc = TRANS_LANGS.find(l => l.code === code);
        const label = lc?.label ?? code.toUpperCase();
        const lt = form.langTexts[code];
        if (!lt?.tipText.trim())      errs.push(`${t('admin.tipFieldObserved')} (${label})`);
        if (!lt?.crecerPaired.trim()) errs.push(`${t('admin.tipFieldGrowthSuggestion')} (${label})`);
      }
    } else {
      if (!form.name.trim())    errs.push(`${t('admin.tipFieldLevelName')} (ES)`);
      if (!form.tipText.trim()) errs.push(`${t('admin.tipFormText')} (ES)`);
      for (const code of form.addedLangs) {
        const lt = form.langTexts[code];
        if (!lt?.name.trim())    errs.push(`${t('admin.tipFieldLevelName')} (${code.toUpperCase()})`);
        if (!lt?.tipText.trim()) errs.push(`${t('admin.tipFormText')} (${code.toUpperCase()})`);
      }
    }

    if (errs.length > 0) { setSaveErrors(errs); return; }
    setSaveErrors([]);

    const translations = buildTranslations(form.addedLangs, form.langTexts, form.tipType);
    if (form.tipType === 'reconocer' && form.crecerPaired)
      translations['es_crecerPaired'] = form.crecerPaired;
    if (form.behavior && form.behavior !== 'skip-validation')
      translations['behaviorId'] = form.behavior;

    const body: Omit<TipItem, 'id'> = {
      fundamentalId: form.fund,
      operator: '>=' as const,
      threshold: parseFloat(form.thrMin) || 0,
      thresholdMax: form.thrMax ? parseFloat(form.thrMax) : null,
      tipType: form.tipType as 'reconocer' | 'crecer',
      tipName: form.name,
      tipText: form.tipText,
      translations,
      active: form.active,
    };

    if (form.editId) {
      updateTip.mutate({ id: form.editId, ...body });
    } else {
      createTip.mutate(body);
    }
    closeForm();
  }

  return (
    <>
      {form.open && (
        <TipFormModal
          form={form}
          setForm={setForm}
          saveErrors={saveErrors}
          onClose={closeForm}
          onSave={handleSave}
          fundamentals={fundamentals}
        />
      )}

      {/* ─────────────── TOOLBAR ─────────────── */}
      <header className="page-header page-header--center">
        <h2 className="page-title">{t('admin.tipsTitle')}</h2>
        <div className="tpl-toolbar">
          <ViewToggle view={view} onViewChange={setView} />
          <input className="tpl-search-input" type="text" placeholder={t('admin.tipsSearchPlaceholder')}
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
          <button className="btn btn--primary btn--sm" onClick={() => openForm()}>
            <i className="fas fa-plus" /> {t('admin.createTip')}
          </button>
        </div>
      </header>

      {loadingTips
        ? <PageSpinner minHeight="120px" />
        : (
        <>
          <div className="tips-filters">
            <select className="tpl-filter-sel" value={fundFilter} onChange={e => { setFundFilter(e.target.value); setPage(1); }}>
              <option value="">{t('admin.tipsAllFundamentals')}</option>
              {fundamentals.map(f => <option key={f.id} value={f.id}>{getFundamentalName(f, lang)}</option>)}
            </select>
            <select className="tpl-filter-sel" value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1); }}>
              <option value="">{t('admin.tipsAllTypes')}</option>
              {TIP_TYPES.map(tt => (
                <option key={tt.id} value={tt.id}>{tt.labels[lang] ?? tt.labels['es']}</option>
              ))}
            </select>
          </div>

          {filtered.length === 0 && (
            <div className="tips-empty"><i className="fas fa-lightbulb" /><p>{t('admin.tipsNoResults')}</p></div>
          )}

      {/* ─────────────── CARDS ─────────────── */}
      {view === 'cards' && filtered.length > 0 && (
        <>
          <div className="tips-cards-grid">
            {paged.map(tip => {
              const fund = fundOf(tip.fundamentalId);
              const isActive = activeMap[tip.id] ?? tip.active;
              return (
                <div key={tip.id} className="tip-card">
                  {fund && (
                    <div className="tip-card-fund" style={{ background: `${fund.color}22`, color: fund.color }}>
                      {getFundamentalName(fund, lang)}
                    </div>
                  )}
                  <div className={`tip-card-type ${tip.tipType === 'reconocer' ? 'rec' : 'crecer'}`}>
                    {tip.tipType === 'reconocer'
                      ? <><i className="fas fa-heart" /> {tipTypeLabel('reconocer')}</>
                      : <><i className="fas fa-seedling" /> {tipTypeLabel('crecer')}</>}
                  </div>
                  <p className="tip-card-name">{localizedTipName(tip, lang)}</p>
                  <p className="tip-card-text">{localizedTipText(tip, lang)}</p>

                  <div className="tip-card-footer">
                    <span className="tip-card-cond">{fmtCond(tip.operator, tip.threshold, tip.thresholdMax)}</span>
                    <div className="adm-actions">
                      <label className="switch" style={{ marginRight: 2 }}>
                        <input type="checkbox" checked={isActive}
                          onChange={e => {
                          const checked = e.target.checked;
                          setActiveMap(m => ({ ...m, [tip.id]: checked }));
                          updateTip.mutate({ ...tip, active: checked });
                        }} />
                        <span className="slider" />
                      </label>
                      <button className="btn-icon" title={t('common.edit')} onClick={() => openForm(tip.id)}>
                        <i className="fas fa-pen" />
                      </button>
                      <button className="btn-icon danger" title={t('common.delete')}
                        onClick={() => setDeleteTip({ id: tip.id, name: tip.tipName })}>
                        <i className="fas fa-trash" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="pager-cards">
            <Pager total={filtered.length} page={page} pageSize={pageSize}
              onPage={setPage} onPageSize={s => { setPageSize(s); setPage(1); }} />
          </div>
        </>
      )}

      {/* ─────────────── LIST ─────────────── */}
      {view === 'list' && filtered.length > 0 && (
        <div className="surveys-table-scroll" style={{ marginTop: 12 }}>
          <table className="adm-table">
            <thead>
              <tr>
                <th>{t('admin.colFundamental')}</th>
                <th>{t('admin.colContent')}</th>
                <th>{t('admin.colType')}</th>
                <th>{t('admin.colCondition')}</th>
                <th>{t('admin.colActive')}</th>
                <th>{t('admin.colActions')}</th>
              </tr>
            </thead>
            <tbody>
              {paged.map(tip => {
                const fund = fundOf(tip.fundamentalId);
                const isActive = activeMap[tip.id] ?? tip.active;
                return (
                  <tr key={tip.id}>
                    <td>
                      {fund && (
                        <span className="tpl-meta-pill" style={{ background: `${fund.color}22`, color: fund.color }}>
                          {getFundamentalName(fund, lang)}
                        </span>
                      )}
                    </td>
                    <td>
                      <ul className="tip-content-bullets">
                        <li><strong style={{ color: 'var(--text-primary)' }}>{localizedTipName(tip, lang)}</strong></li>
                        <li>{localizedTipText(tip, lang)}</li>
                      </ul>
                    </td>
                    <td>
                      <span className={`tip-card-type ${tip.tipType === 'reconocer' ? 'rec' : 'crecer'}`} style={{ display: 'inline-block' }}>
                        {tip.tipType === 'reconocer'
                          ? <><i className="fas fa-heart" /> {tipTypeLabel('reconocer')}</>
                          : <><i className="fas fa-seedling" /> {tipTypeLabel('crecer')}</>}
                      </span>
                    </td>
                    <td><span className="tip-card-cond">{fmtCond(tip.operator, tip.threshold, tip.thresholdMax)}</span></td>
                    <td>
                      <label className="switch">
                        <input type="checkbox" checked={isActive}
                          onChange={e => {
                          const checked = e.target.checked;
                          setActiveMap(m => ({ ...m, [tip.id]: checked }));
                          updateTip.mutate({ ...tip, active: checked });
                        }} />
                        <span className="slider" />
                      </label>
                    </td>
                    <td>
                      <div className="adm-actions">
                        <button className="btn-icon" title={t('common.edit')} onClick={() => openForm(tip.id)}>
                          <i className="fas fa-pen" />
                        </button>
                        <button className="btn-icon danger" title={t('common.delete')}
                          onClick={() => setDeleteTip({ id: tip.id, name: tip.tipName })}>
                          <i className="fas fa-trash" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Pager total={filtered.length} page={page} pageSize={pageSize}
            onPage={setPage} onPageSize={s => { setPageSize(s); setPage(1); }} />
        </div>
      )}

        {deleteTip !== null && (
          <ConfirmModal
            title={t('admin.tipDeleteTitle')}
            message={t('admin.tipDeleteDesc', { name: deleteTip.name })}
            confirmText={t('common.delete')}
            cancelText={t('common.cancel')}
            type="danger"
            onConfirm={() => {
              deleteTipMutation.mutate(deleteTip.id);
              setDeleteTip(null);
            }}
            onClose={() => setDeleteTip(null)}
          />
        )}
        </>
      )}
    </>
  );
}
